import { Infer, t } from 'typegate';

export enum NotificationStatus {
  Unread = 'unread',
  Read = 'read',
}

export enum NotificationType {
  Info = 'info',
  Action = 'action',
}

export enum NotificationEventName {
  OneSpanEmailBounce = 'OneSpanEmailBounce',
  OneSpanCreatePackageError = 'OneSpanCreatePackageError',
  ApplicationSubmitted = 'ApplicationSubmitted',
  NewFeatureLaunch = 'NewFeatureLaunch',
  UnderwritingRequirementsReceived = 'UnderwritingRequirementsReceived',
}

const baseNotificationSchema = t.object(
  t.property('id', t.string),
  t.property('userId', t.string),
  t.property('status', t.enum(NotificationStatus)),
  t.property('createdAt', t.date),
  t.property('updatedAt', t.date),
);

const oneSpanEmailBounceEventMetadataSchema = t.object(
  t.property('applicationId', t.string),
  t.property('leadId', t.number),
  t.property('applicationRefNumber', t.string),
  t.property('email', t.string),
  t.property('signerName', t.string),
  t.property('roles', t.array(t.string)),
);

const oneSpanEmailBounceEventSchema = t.object(
  t.property('event', t.literal(NotificationEventName.OneSpanEmailBounce)),
  t.property('metadata', oneSpanEmailBounceEventMetadataSchema),
);

const applicationSubmittedEventMetadataSchema = t.object(
  t.property('applicationId', t.string),
  t.property('leadId', t.number),
  t.property('applicationRefNumber', t.string),
);

const applicationSubmittedEventSchema = t.object(
  t.property('event', t.literal(NotificationEventName.ApplicationSubmitted)),
  t.property('metadata', applicationSubmittedEventMetadataSchema),
);

const oneSpanCreatePackageErrorEventMetadataSchema = t.object(
  t.property('applicationId', t.string),
  t.property('leadId', t.number),
  t.property('applicationRefNumber', t.string),
);

const oneSpanCreatePackageErrorEventSchema = t.object(
  t.property('event', t.literal(NotificationEventName.OneSpanCreatePackageError)),
  t.property('metadata', oneSpanCreatePackageErrorEventMetadataSchema),
);

const newFeatureEventMetadataSchema = t.object(t.property('name', t.string));

const newFeatureEventSchema = t.object(
  t.property('event', t.literal(NotificationEventName.NewFeatureLaunch)),
  t.property('metadata', newFeatureEventMetadataSchema),
);

const underwritingRequirementsReceivedEventMetadateSchema = t.object(
  t.property('leadId', t.number),
  t.property('applicationRefNumber', t.string),
);

const underwritingRequirementsReceivedEventSchema = t.object(
  t.property('event', t.literal(NotificationEventName.UnderwritingRequirementsReceived)),
  t.property('metadata', underwritingRequirementsReceivedEventMetadateSchema),
);

export const eventSchema = t.union(
  applicationSubmittedEventSchema,
  oneSpanEmailBounceEventSchema,
  oneSpanCreatePackageErrorEventSchema,
  newFeatureEventSchema,
  underwritingRequirementsReceivedEventSchema,
);

const applicationSubmittedNotificationSchema = t
  .intersection(baseNotificationSchema, applicationSubmittedEventSchema)
  .setTypeName('ApplicationSubmittedNotification');
const oneSpanEmailBounceNotificationSchema = t
  .intersection(baseNotificationSchema, oneSpanEmailBounceEventSchema)
  .setTypeName('OneSpanEmailBounceNotification');
const oneSpanCreatePackageErrorNotificationSchema = t
  .intersection(baseNotificationSchema, oneSpanCreatePackageErrorEventSchema)
  .setTypeName('OneSpanCreatePackageErrorNotification');
const newFeatureNotificationSchema = t
  .intersection(baseNotificationSchema, newFeatureEventSchema)
  .setTypeName('NewFeatureNotification');
const underwritingRequirementsReceivedNotificationSchema = t
  .intersection(baseNotificationSchema, underwritingRequirementsReceivedEventSchema)
  .setTypeName('UnderwritingRequirementsReceivedNotification');

export const notificationSchema = t
  .discriminatedUnion(
    'event',
    applicationSubmittedNotificationSchema,
    oneSpanEmailBounceNotificationSchema,
    oneSpanCreatePackageErrorNotificationSchema,
    newFeatureNotificationSchema,
    underwritingRequirementsReceivedNotificationSchema,
  )
  .setTypeName('Notification');

export const partialEventSchema = t.union(
  t.partial(oneSpanEmailBounceEventSchema),
  t.partial(applicationSubmittedEventSchema),
  t.partial(oneSpanCreatePackageErrorEventSchema),
  t.partial(newFeatureEventSchema),
  t.partial(underwritingRequirementsReceivedEventSchema),
);

export const metadataSchema = t.union(
  oneSpanEmailBounceEventMetadataSchema,
  applicationSubmittedEventMetadataSchema,
  oneSpanCreatePackageErrorEventMetadataSchema,
  newFeatureEventMetadataSchema,
  underwritingRequirementsReceivedEventMetadateSchema,
);

export const encryptedNotificationSchema = t.union(
  t.intersection(
    baseNotificationSchema,
    t.intersection(
      t.omit(applicationSubmittedEventSchema, ['metadata']),
      t.object(t.property('encryptedMetadata', t.instanceof(Buffer))),
    ),
  ),
  t.intersection(
    baseNotificationSchema,
    t.intersection(
      t.omit(oneSpanEmailBounceEventSchema, ['metadata']),
      t.object(t.property('encryptedMetadata', t.instanceof(Buffer))),
    ),
  ),
  t.intersection(
    baseNotificationSchema,
    t.intersection(
      t.omit(oneSpanCreatePackageErrorEventSchema, ['metadata']),
      t.object(t.property('encryptedMetadata', t.instanceof(Buffer))),
    ),
  ),
  t.intersection(
    baseNotificationSchema,
    t.intersection(
      t.omit(newFeatureEventSchema, ['metadata']),
      t.object(t.property('encryptedMetadata', t.instanceof(Buffer))),
    ),
  ),
  t.intersection(
    baseNotificationSchema,
    t.intersection(
      t.omit(underwritingRequirementsReceivedEventSchema, ['metadata']),
      t.object(t.property('encryptedMetadata', t.instanceof(Buffer))),
    ),
  ),
);

export const notificationsSchema = t.array(notificationSchema);
export const partialNotificationSchema = t.union(
  t.intersection(t.partial(baseNotificationSchema), t.partial(applicationSubmittedEventSchema)),
  t.intersection(t.partial(baseNotificationSchema), t.partial(oneSpanEmailBounceEventSchema)),
  t.intersection(t.partial(baseNotificationSchema), t.partial(oneSpanCreatePackageErrorEventSchema)),
  t.intersection(t.partial(baseNotificationSchema), t.partial(newFeatureEventSchema)),
  t.intersection(t.partial(baseNotificationSchema), t.partial(underwritingRequirementsReceivedEventSchema)),
);
export const fetchAllNotificationsSchema = t
  .object(t.property('notifications', notificationsSchema), t.property('totalUnreadNotifications', t.number))
  .setTypeName('FetchAllNotifications');

export type Metadata = Infer<typeof metadataSchema>;
export type EventNotification = Infer<typeof eventSchema>;
export type Notification = Infer<typeof notificationSchema>;
export type EncryptedNotification = Infer<typeof encryptedNotificationSchema>;
export type FetchAllNotificationsResponse = Infer<typeof fetchAllNotificationsSchema>;
